import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { contractActions } from './contract.actions';
import { map, mergeMap } from 'rxjs/operators';
import { ContractApiService } from './contract.service';
import { MessageService } from '@letly/ui-common';
import { ToastSeverity } from '@letly/models';
import { Router } from '@angular/router';
import { of } from 'rxjs';

@Injectable()
export class ContractEffects {
  private actions$ = inject(Actions);
  private api = inject(ContractApiService);
  private message = inject(MessageService);
  private router = inject(Router);

  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(contractActions.loadContract),
      mergeMap(({ propertyId, contractId }) =>
        this.api.getContract(propertyId, contractId).pipe(
          map(res => {
            if (res.success) {
              return contractActions.onLoadContract({
                propertyId,
                contractId,
                status: 'success',
                message: null,
                contract: res.data
              });
            } else {
              this.message.showSnackbar(res.niceErrorMessage, ToastSeverity.Error);
              return contractActions.onLoadContract({
                propertyId,
                contractId,
                status: 'error',
                message: res.niceErrorMessage,
                contract: null
              });
            }
          })
        )
      )
    )
  );

  createDraft$ = createEffect(() =>
    this.actions$.pipe(
      ofType(contractActions.createDraft),
      mergeMap(({ propertyId, contractId }) =>
        this.api.createDraft(propertyId, contractId).pipe(
          map(res => {
            if (res.success) {
              const draftId = res.data?.id;
              if (draftId) {
                this.router.navigate(['/landlord', 'property-detail', propertyId, 'contract', draftId]);
              }
              return contractActions.onCreateDraft({
                propertyId,
                contractId,
                status: 'success',
                message: null,
                contract: res.data
              });
            } else {
              this.message.showSnackbar(res.niceErrorMessage, ToastSeverity.Error);
              return contractActions.onCreateDraft({
                propertyId,
                contractId,
                status: 'error',
                message: res.niceErrorMessage,
                contract: null
              });
            }
          })
        )
      )
    )
  );

  submit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(contractActions.submit),
      mergeMap(({ propertyId, contractId }) =>
        this.api.submit(propertyId, contractId).pipe(
          map(res => {
            if (res.success) {
              this.message.showSnackbar($localize`:@@property.landlord-detail.contract-effect.draft-submitted:Draft contract submitted.`, ToastSeverity.Success);
              this.router.navigate(['/landlord', 'property-detail', propertyId, 'contract']);
              return contractActions.onSubmit({
                propertyId,
                contractId,
                status: 'success',
                message: null,
                contract: res.data
              });
            } else {
              this.message.showSnackbar(res.niceErrorMessage, ToastSeverity.Error);
              return contractActions.onSubmit({
                propertyId,
                contractId,
                status: 'error',
                message: res.niceErrorMessage,
                contract: null
              });
            }
          })
        )
      )
    )
  );

  saveDraft$ = createEffect(() =>
    this.actions$.pipe(
      ofType(contractActions.saveDraft),
      mergeMap(({ propertyId, contractId, clauses }) =>
        this.api.updateDraft(propertyId, contractId, clauses).pipe(
          map(res => {
            if (res.success) {
              this.message.showSnackbar($localize`:@@property.landlord-detail.contract-effect.draft-saved:Draft saved.`, ToastSeverity.Success);
              return contractActions.onSaveDraft({
                propertyId,
                contractId,
                status: 'success',
                message: null,
                contract: res.data
              });
            }

            this.message.showSnackbar(res.niceErrorMessage, ToastSeverity.Error);
            return contractActions.onSaveDraft({
              propertyId,
              contractId,
              status: 'error',
              message: res.niceErrorMessage,
              contract: null
            });
          })
        )
      )
    )
  );

  updateDraftAndSubmit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(contractActions.updateDraftAndSubmit),
      mergeMap(({ propertyId, contractId, clauses }) =>
        this.api.updateDraft(propertyId, contractId, clauses).pipe(
          mergeMap(updateRes => {
            if (updateRes.success) {
              return this.api.submit(propertyId, contractId).pipe(
                map(submitRes => {
                  if (submitRes.success) {
                    this.message.showSnackbar($localize`:@@property.landlord-detail.contract-effect.submitted:Contract submitted.`, ToastSeverity.Success);
                    this.router.navigate(['/landlord', 'property-detail', propertyId, 'contract']);
                    return contractActions.onSubmit({
                      propertyId,
                      contractId,
                      status: 'success',
                      message: null,
                      contract: submitRes.data
                    });
                  } else {
                    this.message.showSnackbar(submitRes.niceErrorMessage, ToastSeverity.Error);
                    return contractActions.onSubmit({
                      propertyId,
                      contractId,
                      status: 'error',
                      message: submitRes.niceErrorMessage,
                      contract: null
                    });
                  }
                })
              );
            } else {
              this.message.showSnackbar(updateRes.niceErrorMessage, ToastSeverity.Error);
              return of(contractActions.onSubmit({
                propertyId,
                contractId,
                status: 'error',
                message: updateRes.niceErrorMessage,
                contract: null
              }));
            }
          })
        )
      )
    )
  );
}
